import React, { useEffect, useRef } from "react";
import { createResizeHandler } from "../utils/cornerstone/resizeHandler";

interface ZoomControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
}

const ZoomControls: React.FC<ZoomControlsProps> = ({ onZoomIn, onZoomOut }) => (
  <div
    style={{
      position: "absolute",
      top: "8px",
      right: "8px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      zIndex: 10,
    }}
    className="zoom-controls"
  >
    <button
      onClick={onZoomIn}
      style={{
        width: "32px",
        height: "32px",
        borderRadius: "4px",
        border: "1px solid #666",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "#fff",
        fontSize: "16px",
        fontWeight: "bold",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.9)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      }}
      title="Zoom In"
    >
      +
    </button>
    <button
      onClick={onZoomOut}
      style={{
        width: "32px",
        height: "32px",
        borderRadius: "4px",
        border: "1px solid #666",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "#fff",
        fontSize: "16px",
        fontWeight: "bold",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.9)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      }}
      title="Zoom Out"
    >
      −
    </button>
  </div>
);

interface ViewportContainerProps {
  element1Ref: React.RefObject<HTMLDivElement>;
  element2Ref: React.RefObject<HTMLDivElement>;
  element3Ref: React.RefObject<HTMLDivElement>;
  visibleViewports: {
    axial: boolean;
    sagittal: boolean;
    coronal: boolean;
  };
  onZoom?: (viewportId: string, direction: "in" | "out") => void;
}

const ViewportContainer: React.FC<ViewportContainerProps> = ({
  element1Ref,
  element2Ref,
  element3Ref,
  visibleViewports,
  onZoom,
}) => {
  // Reference to the container element
  const containerRef = useRef<HTMLDivElement>(null);

  // Set up a resize observer to detect size changes in the container
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeHandler = createResizeHandler("myRenderingEngine", 200);

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      console.log("ViewportContainer: ResizeObserver detected size change");
      resizeHandler();
    });

    // Start observing the container element
    resizeObserver.observe(containerRef.current);

    // Clean up
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  return (
    <div
      ref={containerRef}
      style={{ display: "flex", flexDirection: "row", height: "100%", position: "relative" }}
      className="viewports-container"
    >
      {/* Axial viewport on the left */}
      <div
        ref={element1Ref}
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "#000",
          position: "relative",
          display: visibleViewports.axial ? undefined : "none",
          borderRight: "2px solid #ccc",
        }}
        className="viewport viewport-axial"
        onContextMenu={(e) => {
          e.preventDefault();
          // Custom right-click logic can go here
        }}
      >
        {onZoom && visibleViewports.axial && (
          <ZoomControls
            onZoomIn={() => onZoom("CT_AXIAL", "in")}
            onZoomOut={() => onZoom("CT_AXIAL", "out")}
          />
        )}
      </div>

      {/* 2D viewports stacked vertically on the right */}
      <div
        style={{
          display:
            visibleViewports.sagittal || visibleViewports.coronal
              ? "flex"
              : "none",
          flexDirection: "column",
          width: "65%",
        }}
        className="viewports-2d"
      >
        <div
          ref={element2Ref}
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#000",
            position: "relative",
            display: visibleViewports.sagittal ? undefined : "none",
            borderBottom: "2px solid #ccc",
          }}
          className="viewport viewport-sagittal"
          onContextMenu={(e) => {
            e.preventDefault();
            // Custom right-click logic can go here
          }}
        >
          {onZoom && visibleViewports.sagittal && (
            <ZoomControls
              onZoomIn={() => onZoom("CT_SAGITTAL", "in")}
              onZoomOut={() => onZoom("CT_SAGITTAL", "out")}
            />
          )}
        </div>
        <div
          ref={element3Ref}
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#000",
            position: "relative",
            display: visibleViewports.coronal ? undefined : "none",
          }}
          className="viewport viewport-coronal"
          onContextMenu={(e) => {
            e.preventDefault();
            // Custom right-click logic can go here
          }}
        >
          {onZoom && visibleViewports.coronal && (
            <ZoomControls
              onZoomIn={() => onZoom("CT_CORONAL", "in")}
              onZoomOut={() => onZoom("CT_CORONAL", "out")}
            />
          )}
        </div>
      </div>

    </div>
  );
};

export default ViewportContainer;
